import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  MapPin,
  FileText,
  X,
} from "lucide-react";
import { formatAppointmentTime, formatWeekdayLong } from "@/utils/date";
import {
  getCancellationPolicyNotice,
  getRefundOutcomeNotice,
  type CancellationPolicyNotice,
} from "@/utils/cancellation-policy";
import {
  useCustomerAppointmentById,
  useCustomerProfile,
  useCancelCustomerAppointment,
} from "@/api/hooks/customer.hooks";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { VisitType, NOTICE_CLASSES, PATIENT_STATUS_NOTICES } from "@/api/enums";
import Button from "@/components/ui/Button";
import { AppointmentLocation } from "@/components/ui/AppointmentLocation";
import { useToast } from "@/hooks/use-toast";

/**
 * The cost of cancelling right now, in the patient's own terms. Rendered both
 * beside the cancel button and inside the confirmation step, so the consequence
 * is on screen at the moment the patient commits, not only before it.
 */
function CancellationPolicyNotice({
  policy,
}: {
  policy: CancellationPolicyNotice | null;
}) {
  if (!policy) return null;

  const classes = NOTICE_CLASSES[policy.tone];

  return (
    <div className={`${classes.wrapper} mb-4`}>
      <p className={`${classes.text} font-medium`}>{policy.headline}</p>
      <p className={`${classes.text} mt-1`}>{policy.detail}</p>
    </div>
  );
}

export default function PatientAppointmentDetail() {
  const { id } = useParams({ from: "/client-area/appointments/$id" });
  const navigate = useNavigate();
  const {
    data: appointment,
    isLoading,
    error,
    refetch,
  } = useCustomerAppointmentById(id);
  const { data: profile } = useCustomerProfile();
  const cancelAppointment = useCancelCustomerAppointment();
  const { toast } = useToast();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Whether cancelling is still possible is the API's call, not ours: it also
  // covers an appointment whose start time has passed, which a status check
  // alone would miss.
  const canCancel = appointment?.cancellationPolicy.canCancel ?? false;
  const statusNotice = appointment
    ? PATIENT_STATUS_NOTICES[appointment.status]
    : null;
  // What cancelling right now would cost, shown next to the cancel button so
  // the patient never has to guess before committing.
  const cancellationPolicy =
    appointment && canCancel
      ? getCancellationPolicyNotice(appointment.cancellationPolicy)
      : null;
  // What already happened to the fee, once the appointment is over.
  const refundOutcome = appointment ? getRefundOutcomeNotice(appointment) : null;

  const handleCancel = async () => {
    if (!id) return;
    try {
      await cancelAppointment.mutateAsync(id);
      toast({
        title: "Success",
        description: "Appointment canceled successfully",
      });
      setShowCancelConfirm(false);
      navigate({ to: "/client-area/appointments" });
    } catch {
      toast({
        title: "Error",
        description: "Failed to cancel appointment",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="bg-white rounded-xl border border-border p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <button
            onClick={() => navigate({ to: "/client-area/appointments" })}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md px-2 py-1 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Appointments
          </button>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              Appointment Not Found
            </h2>
            <p className="text-muted-foreground mt-1">
              The requested appointment could not be found.
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-border p-12 text-center">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Appointment Not Found
          </h3>
          <p className="text-muted-foreground mb-6">
            The requested appointment could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <button
          onClick={() => navigate({ to: "/client-area/appointments" })}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md px-2 py-1 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Appointments
        </button>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Appointment Details
          </h2>
          <p className="text-muted-foreground mt-1">
            View your appointment information
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {appointment.specialistName}
                </h3>
                <AppointmentLocation
                  address={appointment.locationAddress}
                  isTelehealth={appointment.visitType === VisitType.Telemedicine}
                />
              </div>
              <StatusBadge status={appointment.status} type="primary" />
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Date & Time</p>
                  <p className="font-medium text-foreground">
                    {formatWeekdayLong(appointment.startTime)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatAppointmentTime(appointment.startTime)} (
                    {appointment.durationInMinutes} minutes)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Visit Type</p>
                  <p className="font-medium text-foreground">
                    {appointment.visitType === VisitType.Telemedicine
                      ? "Telemedicine"
                      : "In-Person"}
                  </p>
                </div>
              </div>

              {appointment.symptoms && (
                <div className="flex items-start gap-4">
                  <FileText className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Reason for Visit
                    </p>
                    <p className="text-foreground">{appointment.symptoms}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Your Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium text-foreground">
                    {`${profile?.firstName || ""} ${profile?.lastName || ""}`.trim() ||
                      "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground break-all">
                    {profile?.email || "N/A"}
                  </p>
                </div>
              </div>
              {profile?.phoneNumber && (
                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium text-foreground">
                      {profile.phoneNumber}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {statusNotice && (
            <div className={NOTICE_CLASSES[statusNotice.tone].wrapper}>
              <p className={NOTICE_CLASSES[statusNotice.tone].text}>
                {statusNotice.message}
              </p>
            </div>
          )}

          {refundOutcome && (
            <div className={NOTICE_CLASSES[refundOutcome.tone].wrapper}>
              <p className={NOTICE_CLASSES[refundOutcome.tone].text}>
                {refundOutcome.message}
              </p>
            </div>
          )}

          {canCancel && !showCancelConfirm && (
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Actions
              </h3>
              <CancellationPolicyNotice policy={cancellationPolicy} />
              <Button
                variant="destructive"
                onClick={() => {
                  // The policy was computed when this page loaded, and a tab
                  // left open can sit right through the notice window. Ask
                  // again on the way into the confirmation step so the numbers
                  // the patient commits on are the ones the API will act on.
                  setShowCancelConfirm(true);
                  void refetch();
                }}
                className="w-full"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel Appointment
              </Button>
            </div>
          )}

          {showCancelConfirm && (
            <div className="bg-white rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Cancel Appointment
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Are you sure you want to cancel this appointment? This action
                cannot be undone.
              </p>
              <CancellationPolicyNotice policy={cancellationPolicy} />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1"
                  disabled={cancelAppointment.isPending}
                >
                  Keep Appointment
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  className="flex-1"
                  disabled={cancelAppointment.isPending}
                >
                  {cancelAppointment.isPending ? "Canceling..." : "Yes, Cancel"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
